<template>
  <div
    id="app"
    class="min-h-screen bg-primary-100 text-secondary-800 antialiased p-4 sm:p-6"
  >
    <!-- INITIALIZING -->
    <div
      v-if="authStore.isInitializing"
      class="grid min-h-[60vh] place-items-center"
    >
      <div
        class="w-full max-w-sm rounded-xl border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <span
            class="inline-flex h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
          ></span>
          <div>
            <p class="text-base font-semibold">{{ t('app.loggingIn') }}</p>
            <p class="text-sm text-neutral-500">{{ t('app.pleaseWait') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="authStore.error"
      class="grid min-h-[60vh] place-items-center"
    >
      <div
        class="w-full max-w-md rounded-xl border border-accent-three-300 bg-white p-6 shadow-sm"
      >
        <h2 class="mb-2 text-xl font-semibold">{{ t('app.loginFailed') }}</h2>
        <p class="mb-6 text-neutral-600">{{ authStore.error }}</p>
        <button
          class="rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-800 active:bg-primary-800"
          @click="retryLogin"
        >
          {{ t('app.retry') }}
        </button>
      </div>
    </div>

    <!-- AUTHENTICATED -->
    <div v-else-if="authStore.isAuthenticated" class="mx-auto w-full max-w-4xl">
      <!-- Header -->
      <HeaderStatusContainer :initialization-failed="initializationFailed" />

      <!-- AI Assistant Panel -->
      <div class="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div
          class="border-b border-neutral-100 px-5 py-3 flex justify-between items-center"
        >
          <h2 class="text-lg font-semibold text-secondary-800">Pete</h2>
          <button
            class="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            :disabled="
              messageStore.customerMessages.length === 0 ||
              messageStore.aiResponses.length < 1 ||
              messageStore.isSummaryGenerating ||
              messageStore.isAiResponseProcessing
            "
            @click="generateSummary"
          >
            <span
              v-if="messageStore.isSummaryGenerating"
              class="inline-flex h-3 w-3 animate-spin rounded-full border border-primary-500 border-t-transparent"
            ></span>
            <span>{{
              messageStore.isSummaryGenerating
                ? t('app.generatingShort')
                : t('app.summary')
            }}</span>
          </button>
        </div>

        <div class="p-5">
          <!-- Resume Decision Prompt -->
          <div
            v-if="messageStore.pendingResumeDecision"
            class="mb-4 rounded-lg border-2 border-primary-400 bg-primary-50 p-4 shadow-sm"
          >
            <p class="text-lg font-bold text-primary-800 mb-1">
              {{ t('resume.title') }}
            </p>
            <p class="text-sm text-primary-800 mb-3">
              {{ t('resume.prompt') }}
            </p>

            <div class="mb-3 rounded-md border border-primary-200 bg-white p-3">
              <p class="text-xs font-semibold text-primary-600 mb-1">
                {{ t('resume.previousSummary') }}
              </p>
              <div
                v-if="messageStore.isPreviousSegmentSummaryGenerating"
                class="flex items-center gap-2 text-sm text-primary-700"
              >
                <span
                  class="inline-flex h-3 w-3 animate-spin rounded-full border border-primary-500 border-t-transparent"
                ></span>
                <span>{{ t('resume.generatingSummary') }}</span>
              </div>
              <p
                v-else-if="messageStore.previousSegmentSummary"
                class="whitespace-pre-line text-sm text-secondary-800"
              >
                {{ messageStore.previousSegmentSummary }}
              </p>
              <p v-else class="text-sm text-neutral-500">
                {{ t('resume.noSummary') }}
              </p>
            </div>

            <div class="flex flex-col gap-2 sm:flex-row">
              <button
                class="rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100 cursor-pointer"
                @click="resumeContinue"
              >
                {{ t('resume.continue') }}
              </button>
              <button
                class="rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100 cursor-pointer"
                @click="resumeNew"
              >
                {{ t('resume.new') }}
              </button>
              <button
                class="rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-800 cursor-pointer"
                @click="resumeNewWithSummary"
              >
                {{ t('resume.newWithSummary') }}
              </button>
            </div>
          </div>

          <!-- Summary Section -->
          <div
            v-if="
              messageStore.hasSummary ||
              messageStore.isSummaryGenerating ||
              messageStore.summaryError
            "
            class="mb-4"
          >
            <SummaryContainer />
          </div>

          <!-- Processing State -->
          <div
            v-if="messageStore.isAiResponseProcessing"
            class="rounded-lg border-2 border-primary-300 bg-primary-100 p-4 mb-4 shadow-lg"
          >
            <div class="flex items-center gap-3">
              <span
                class="inline-flex h-6 w-6 animate-spin rounded-full border-3 border-primary-500 border-t-transparent"
              ></span>
              <div>
                <p class="font-bold text-primary-800 text-lg">
                  {{ t('app.processing') }}
                </p>
                <p class="text-base text-primary-800 font-medium">
                  {{ processingStatusText }}
                </p>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div
            v-if="messageStore.aiResponseError"
            class="rounded-lg border border-accent-three-300 bg-accent-three-100 p-4 mb-4"
          >
            <div class="flex items-start gap-3">
              <svg
                class="h-5 w-5 text-accent-three-500 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <div>
                <p class="font-medium text-accent-three-800">
                  {{ t('app.errorProcessing') }}
                </p>
                <p class="text-sm text-accent-three-800 mt-1">
                  {{ messageStore.aiResponseError }}
                </p>
                <button
                  class="mt-2 text-sm text-accent-three-500 hover:text-accent-three-800 underline"
                  @click="messageStore.clearAiResponse()"
                >
                  {{ t('app.dismiss') }}
                </button>
              </div>
            </div>
          </div>

          <!-- No Activity State -->
          <div
            v-if="
              !messageStore.isAiResponseProcessing &&
              !messageStore.hasAiResponse &&
              !messageStore.aiResponseError
            "
            class="grid place-items-center py-12 text-neutral-500"
          >
            <div class="text-center">
              <svg
                class="mx-auto h-12 w-12 text-neutral-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z M5 6a2 2 0 012-2h6a2 2 0 012 2v2H7a2 2 0 00-2 2v6z"
                />
              </svg>
              <p class="text-lg font-medium text-secondary-800 mb-2">
                {{ t('response.waitingForMessages') }}
              </p>
              <p class="text-sm text-neutral-500">
                {{ t('response.waitingForMessagesHint') }}
              </p>
            </div>
          </div>

          <!-- Waiting for Messages State -->
          <div
            v-else-if="
              messageStore.isAiResponseProcessing &&
              messageStore.hasAiResponse &&
              messageStore.aiResponseError &&
              messageStore.customerMessages.length > 0
            "
            class="grid place-items-center py-12 text-neutral-500"
          >
            <div class="text-center">
              <svg
                class="mx-auto h-8 w-8 text-neutral-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <p class="text-sm text-neutral-600">
                {{ t('app.readyToAssist') }}
              </p>
            </div>
          </div>

          <!-- AI Response -->
          <AiResponseContainer />
        </div>
      </div>
    </div>

    <!-- NOT AUTHENTICATED -->
    <div v-else class="grid min-h-[60vh] place-items-center text-center">
      <div
        class="w-full max-w-sm rounded-xl border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <h2 class="mb-3 text-xl font-semibold text-secondary-800">
          {{ t('app.notAuthenticated') }}
        </h2>
        <button
          class="rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-800 active:bg-primary-800"
          @click="login"
        >
          {{ t('app.login') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/messages';
import { IntegrationCoordinator } from '@/services/IntegrationCoordinator';
import { BackendWebSocketService } from '@/services/BackendWebSocketService';
import { PROCESSING_STAGE } from '@/constants';
import HeaderStatusContainer from '@/components/header/HeaderStatusContainer.vue';
import AiResponseContainer from '@/components/ai-response/AiResponseContainer.vue';
import SummaryContainer from '@/components/ai-response/SummaryContainer.vue';

const { t, locale } = useI18n();
const authStore = useAuthStore();
const messageStore = useMessageStore();

const listEl = ref<HTMLElement | null>(null);
const initializationFailed = ref(false);

// Human-readable text for the processing pane: retry copy when retrying, the
// latest verbose middleware activity when available, else a stage fallback.
const processingStatusText = computed(() => {
  const proc = messageStore.aiResponseProcessing;
  if (
    proc.stage === PROCESSING_STAGE.RETRYING &&
    proc.attempt &&
    proc.maxAttempts
  ) {
    return t('middleware.retrying', {
      attempt: proc.attempt,
      max: proc.maxAttempts,
    });
  }
  if (messageStore.currentActivityText) {
    return messageStore.currentActivityText;
  }
  if (proc.stage === PROCESSING_STAGE.SENDING) {
    return t('app.sending');
  }
  if (proc.stage === PROCESSING_STAGE.GENERATING) {
    return t('app.generating');
  }
  return t('app.working');
});

onMounted(async () => {
  await authStore.initialize();

  // Apply the agent locale once authentication has resolved agentLang.
  if (authStore.agentLang) {
    locale.value = authStore.agentLang;
  }

  if (authStore.isAuthenticated) {
    const success = await IntegrationCoordinator.initialize();
    if (!success) {
      initializationFailed.value = true;
    }
  }

  nextTick(scrollToBottom);
});

onUnmounted(() => {
  IntegrationCoordinator.cleanup();
});

// Watch for new messages and auto-scroll
watch(
  () => messageStore.messages.length,
  async () => {
    await nextTick();
    scrollToBottom();
  }
);

const login = () => {
  authStore.initialize();
};

const retryLogin = () => {
  authStore.error = null;
  authStore.initialize();
};

const generateSummary = async () => {
  const success = await BackendWebSocketService.sendSummaryRequest();
  if (!success) {
    messageStore.setSummaryError(t('app.connectionFailed'));
  }
};

// Resume-decision handlers. Resolve/ack the boundary only after the worker
// send succeeds, so a failed send leaves the prompt open (and the boundary
// un-acked) instead of silently suppressing a future re-prompt.
const resumeContinue = async () => {
  const boundaryId = messageStore.pendingBoundaryId;
  const success = await BackendWebSocketService.sendSegmentContinue(boundaryId);
  if (success) {
    messageStore.resolveResumeDecision('continue');
  }
};

const resumeNew = async () => {
  const boundaryId = messageStore.pendingBoundaryId;
  const success = await BackendWebSocketService.sendSegmentReset({
    keepSummaryAsContext: false,
    boundaryId,
  });
  if (success) {
    messageStore.resolveResumeDecision('reset');
  }
};

const resumeNewWithSummary = async () => {
  const boundaryId = messageStore.pendingBoundaryId;
  const success = await BackendWebSocketService.sendSegmentReset({
    keepSummaryAsContext: true,
    boundaryId,
  });
  if (success) {
    messageStore.resolveResumeDecision('reset_with_summary');
  }
};

function scrollToBottom() {
  const el = listEl.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}
</script>
